// Shared chart configuration constants
// Font sizes correspond to tailwind scale: 5xs=8px, 4xs=10px, 3xs=12px, 2xs=14.4px
export const CHART_CONSTANTS = {
    // Axis styling
    AXIS_FONT_SIZE: 11, // ~3xs (0.75rem = 12px, we use 11px for compact display)
    AXIS_TICK_MARGIN: 8,
    Y_AXIS_WIDTH: 35,
    Y_AXIS_TICK_MARGIN: 5,

    // Chart margins (non-printMode)
    MARGIN: {
        top: 5,
        right: 10,
        left: 0,
        bottom: 5,
    },

    // Print mode margins
    PRINT_MARGIN: {
        top: 5,
        right: 30,
        left: 20,
        bottom: 5,
    },

    // Chart dimensions
    CHART_HEIGHT: 400,
    PRINT_WIDTH: 700,
    PRINT_HEIGHT: 400,

    // Cursor styling
    CURSOR: {
        stroke: "#6366f1",
        strokeWidth: 2,
        strokeDasharray: "5 5",
    },
} as const;

/** Optional axis styling (e.g. responsive tick size from container width). */
export type ChartAxisOverrides = {
    axisFontSize?: number;
    yAxisWidth?: number;
    axisTickMargin?: number;
    yAxisTickMargin?: number;
};

export const CHART_GRID_PROPS = {
    strokeDasharray: "3 3",
    vertical: false,
} as const;

// Helper to create standard axis props
export function createXAxisProps(printMode: boolean = false, overrides?: ChartAxisOverrides) {
    const fontSize = overrides?.axisFontSize ?? CHART_CONSTANTS.AXIS_FONT_SIZE;
    return {
        dataKey: "timing",
        tickLine: false,
        tickMargin: overrides?.axisTickMargin ?? CHART_CONSTANTS.AXIS_TICK_MARGIN,
        axisLine: false,
        tick: { fontSize, fill: "currentColor" },
        className: printMode
            ? "print:!text-black text-slate-600 dark:text-slate-400"
            : "text-slate-600 dark:text-slate-400",
    };
}

/**
 * Recharts 2 has no auto Y-axis width, so labels wider than `width` are clipped on the left.
 * Estimates the width needed for the longest tick label, never below `minWidth`.
 */
export function estimateYAxisWidth(
    labels: Array<string | number>,
    {
        fontSize = CHART_CONSTANTS.AXIS_FONT_SIZE,
        tickMargin = CHART_CONSTANTS.Y_AXIS_TICK_MARGIN,
        minWidth = CHART_CONSTANTS.Y_AXIS_WIDTH,
    }: { fontSize?: number; tickMargin?: number; minWidth?: number } = {}
) {
    const longest = labels.reduce<number>((max, label) => Math.max(max, String(label).length), 0);
    return Math.max(minWidth, Math.ceil(longest * fontSize * 0.65 + tickMargin + 4));
}

export function createYAxisProps(
    domain: [number, number],
    ticks: number[] | undefined,
    printMode: boolean = false,
    overrides?: ChartAxisOverrides & { formatTick?: (value: number) => string }
) {
    const fontSize = overrides?.axisFontSize ?? CHART_CONSTANTS.AXIS_FONT_SIZE;
    const tickMargin = overrides?.yAxisTickMargin ?? CHART_CONSTANTS.Y_AXIS_TICK_MARGIN;
    const formatTick = overrides?.formatTick ?? String;
    return {
        domain,
        ...(ticks != null ? { ticks } : {}),
        tickLine: false,
        axisLine: false,
        tickMargin,
        tick: { fontSize, fill: "currentColor" },
        width: estimateYAxisWidth((ticks ?? domain.map((value) => Number(value.toFixed(2)))).map(formatTick), {
            fontSize,
            tickMargin,
            minWidth: overrides?.yAxisWidth ?? CHART_CONSTANTS.Y_AXIS_WIDTH,
        }),
        className: printMode
            ? "print:!text-black text-slate-600 dark:text-slate-400"
            : "text-slate-600 dark:text-slate-400",
    };
}

export function createTooltipProps() {
    return {
        cursor: CHART_CONSTANTS.CURSOR,
        wrapperStyle: { outline: "none" },
        content: () => null,
    };
}
